import type { InjectionKey } from "vue";
import Swal from "sweetalert2";
import type { SweetAlertResult, SweetAlertPosition } from "sweetalert2";
import type { ComposerTranslation } from "vue-i18n";

// to override the defaul Swal icons you can add a key-value pair with toast type as key and an stringified icon as value
const iconsHTML = {
   warning: `<div class="stroke-current w-10 h-10"><svg viewBox="0 0 24 24"><path d="M12.0015 9V11M12.0015 15H12.0115M5.0733 19H18.9297C20.4693 19 21.4316 17.3333 20.6618 16L13.7336 4C12.9637 2.66667 11.0392 2.66667 10.2694 4L3.34125 16C2.57145 17.3333 3.5337 19 5.0733 19Z" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></div>`,
};

interface ToastProps {
   title?: string;
   text?: string;
   html?: string;
   type?: "warning" | "success" | "error" | "info" | "question";
   // position and style
   position?: SweetAlertPosition;
   padding?: string;
   width?: number;
   // buttons
   showConfirmButton?: boolean;
   confirmButtonText?: string;
   preConfirm?: any;
   showDenyButton?: boolean;
   denyButtonText?: string;
   preDeny?: any;
   showLoaderOnDeny?: boolean;
   showCancelButton?: boolean;
   cancelButtonText?: string;
   allowOutsideClick?: boolean;
   allowEnterKey?: boolean;
   // misc
   timer?: number;
   timerProgressBar?: boolean;
   didOpen?: () => void;
   willClose?: () => void;
}

type UseToastType = (props: ToastProps) => Promise<SweetAlertResult<any>>;

export const getUseToast =
   (t: ComposerTranslation) =>
   async ({
      title = undefined,
      text = "",
      html = "",
      type = undefined, // no icon type will show by default
      // position and style
      padding = undefined,
      width = undefined,
      position = "center",
      // confirm button
      showConfirmButton = true,
      confirmButtonText = t("system.button_ok"),
      preConfirm = undefined,
      // deny button
      showDenyButton = false,
      denyButtonText = "",
      showLoaderOnDeny = true,
      preDeny = undefined,
      // cancel button
      showCancelButton = false,
      cancelButtonText = t("system.button_cancel"),
      // button config
      allowOutsideClick = true,
      timerProgressBar = false,
      // misc
      timer = undefined,
      didOpen = undefined,
      willClose = undefined,
   }: ToastProps): Promise<SweetAlertResult<any>> => {
      return Swal.fire({
         title,
         text,
         html,
         position,
         width,
         padding,
         icon: type,
         iconHtml: iconsHTML[type],
         customClass: {
            container: "text-regular",
            title: "!font-weight-400 !text-2xl !mb-4",
            htmlContainer: "!text-black !text-base",
            confirmButton: "toast-btn-common toast-confirm-btn !bg-brand-dark",
            cancelButton: "toast-btn-common toast-cancel-btn !bg-grey-10 !text-black",
            denyButton: "toast-btn-common toast-deny-btn",
            timerProgressBar: "bg-success-fg",
         },
         // confirm button
         showConfirmButton,
         confirmButtonText,
         preConfirm,
         // deny button
         showDenyButton,
         denyButtonText,
         showLoaderOnDeny,
         preDeny,
         // cancel button
         showCancelButton,
         cancelButtonText,
         // button config
         allowOutsideClick: () => {
            // dont allow clicking outside when loading
            return !Swal.isLoading() || allowOutsideClick;
         },
         // misc
         timer,
         timerProgressBar,
         didOpen,
         willClose,
      });
   };

export const toastSwal = Swal;

export const useToastKey = Symbol() as InjectionKey<UseToastType>;

export const vueUseToast = {
   install: (app, { $t }) => {
      const useToast = getUseToast($t);
      app.config.globalProperties.useToast = useToast;
      app.provide(useToastKey, useToast); // make function available to vue3 script setup
   },
};
